<!-- SignUp.vue -->
<template>
  <v-app>
  <div class="page-background">
    <div class="layout-container">
      <!-- New Container with MN Logo, Title, Description, and Copyright -->
      <div class="info-container">
        <img src="../assets/MN logo.png" alt="MN Logo" class="mn-logo" />
        <h2>Match Reports</h2>
        <p class="MR-description">Match Reports is a streamlined platform that collects and stores match scores, misconduct, game comments, and behavior assessments from coaches and spectators. It organizes data by referee, team, league, season, and referee organization, while seamlessly integrating with ArbiterSports to automatically import game schedules and related details.</p>
        <p class="copywrite">2023-25 Protected by Miller Newlon. All Rights Reserved. Unauthorized reproduction or distribution is prohibited.</p>
      </div>

      <div class="form-container">
        <h1>Sign Up</h1>

        <div v-if="signupError" class="error-message">{{ signupError }}</div>

        <form @submit.prevent="signup">
          <div class="input-container">
            <input v-model="inputToken" type="text" placeholder="Token" required aria-label="Token" />
          </div>
          <div class="input-container">
            <input v-model="email" type="email" placeholder="Email" required aria-label="Email address" />
          </div>
          <div class="input-container">
            <input v-model="password" :type="showPassword ? 'text' : 'password'" placeholder="Password" required aria-label="Password" />
          </div>
          <div class="input-container">
            <input 
              v-model="confirmPassword" 
              :type="showPassword ? 'text' : 'password'" 
              placeholder="Confirm Password" 
              required 
              :class="password !== confirmPassword && confirmPassword.length > 0 ? 'error' : 'success'" 
            />
          </div>

          <!-- Show password toggle checkbox -->
          <div class="input-container checkbox-container">
            <label>
              <input type="checkbox" v-model="showPassword" /> Show Password
            </label>
          </div>

          <!-- Display a message if passwords don't match -->
          <div v-if="password !== confirmPassword && confirmPassword.length > 0" class="error-message">
            Passwords do not match.
          </div>

          <!-- signup error messages -->
          <div v-if="signupError" class="error-message">{{ signupError }}</div>

          <!-- Disable the signup button if passwords don't match -->
          <button type="submit" :disabled="password !== confirmPassword || loading">
            <span v-if="loading" class="spinner"></span> 
            <span v-else>Sign Up</span>
          </button>
        </form>

        <p class="newuser">Already have an account? <router-link to="/">Login</router-link></p>
      </div>
    </div>
  </div>
  
  <!-- Success Dialog -->
  <v-dialog v-model="showDialog" max-width="500" persistent>
    <v-card>
      <v-card-title class="headline">Verify Your Email</v-card-title>
      <v-card-text>
        <p>Sign up successful! A verification email has been sent to <strong>{{ email }}</strong>.</p>
        <p>Please verify your email within <strong>60 minutes</strong> to activate your account.</p>
        <div v-if="claimSetStatus" class="mt-3">
          <v-chip :color="claimSetStatus.success ? 'success' : 'error'" variant="flat" class="mb-2">
            {{ claimSetStatus.message }}
          </v-chip>
        </div>
        <!-- Debug information (remove in production) -->
        <div v-if="debugInfo" class="mt-3 pa-2 bg-grey-lighten-4 rounded">
          <div class="text-caption">Debug Info:</div>
          <div class="text-caption">{{ debugInfo }}</div>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-btn color="primary" @click="resendVerification" :disabled="resending">
          <span v-if="resending">Sending...</span>
          <span v-else>Resend Verification Email</span>
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn text @click="goToLogin">Go to Login</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Loading Overlay -->
  <v-overlay v-model="processingClaims" class="align-center justify-center" persistent>
    <div class="d-flex flex-column align-center ga-2">
      <v-progress-circular indeterminate size="40" />
      <div>Setting up your account...</div>
      <div class="text-caption">This may take a few seconds</div>
      <div v-if="retryCount" class="text-caption">Retry attempt: {{ retryCount }}/5</div>
    </div>
  </v-overlay>
</v-app>
</template>

<script>
import { ref, computed } from 'vue';
import { createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, db } from '../firebase';
import { collection, collectionGroup, query, where, getDoc, getDocs, doc, setDoc, limit, serverTimestamp } from 'firebase/firestore';
import { useRouter } from 'vue-router';

export default {
  setup() {
    const inputToken = ref('');
    const email = ref('');
    const password = ref('');
    const confirmPassword = ref('');
    const signupError = ref('');
    const showPassword = ref(false);
    const router = useRouter();
    const loading = ref(false);
    const processingClaims = ref(false);
    const claimSetStatus = ref(null);
    const retryCount = ref(0);
    const debugInfo = ref(null);

    const isFormValid = computed(() =>
      password.value === confirmPassword.value &&
      email.value !== "" &&
      inputToken.value !== ""
    );

    const functions = getFunctions(undefined, "us-central1");
    const setInitialClaims = httpsCallable(functions, "setInitialClaims");
    const debugClaims = httpsCallable(functions, "debugClaims");

    // ✅ ENHANCED: More robust claim verification with detailed logging
    const verifyClaimsWithRetry = async (user, maxRetries = 8, initialDelay = 1500) => {
      let delay = initialDelay;
      retryCount.value = 0;
      
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          retryCount.value = attempt;
          console.log(`🔄 Claim verification attempt ${attempt}/${maxRetries} (delay: ${delay}ms)`);
          
          // ✅ STEP 1: Force token refresh to get latest claims
          console.log("📱 Refreshing ID token...");
          await user.getIdToken(true);
          
          // ✅ STEP 2: Get token result with detailed claims
          console.log("🔍 Getting ID token result...");
          const idTokenResult = await user.getIdTokenResult(true);
          const claims = idTokenResult.claims;
          
          console.log(`📋 Attempt ${attempt} - All claims found:`, claims);
          console.log(`📋 Attempt ${attempt} - orgRoles:`, claims.orgRoles);
          console.log(`📋 Attempt ${attempt} - Boolean claims:`, {
            ADMIN: claims.ADMIN,
            Referee: claims.Referee,
            RefereeCoach: claims.RefereeCoach
          });
          console.log(`📋 Attempt ${attempt} - Role claim:`, claims.role);
          console.log(`📋 Attempt ${attempt} - Organization claim:`, claims.organization);
          
          // ✅ STEP 3: Check for claims in multiple possible formats
          const roleClaim = claims.role || claims.Role || claims.ROLE;
          const orgClaim = claims.organization || claims.Organization || claims.ORGANIZATION;
          
          console.log(`🔎 Extracted role: ${roleClaim}, organization: ${orgClaim}`);
          
          // Check NEW orgRoles structure
            let foundRoleInOrgRoles = null;
            let foundOrgInOrgRoles = null;
            
            if (claims.orgRoles && typeof claims.orgRoles === 'object') {
              for (const [org, roles] of Object.entries(claims.orgRoles)) {
                if (Array.isArray(roles) && roles.length > 0) {
                  foundRoleInOrgRoles = roles[0]; // Use first role
                  foundOrgInOrgRoles = org;
                  break;
                }
              }
            }
            
            // Check boolean claims (ADMIN: true, Referee: true, etc.)
            let foundRoleInBoolean = null;
            const validRoles = ['ADMIN', 'Referee', 'RefereeCoach'];
            for (const role of validRoles) {
              if (claims[role] === true) {
                foundRoleInBoolean = role;
                break;
              }
            }
            
            // Use the first available claim format
            const finalRole = roleClaim || foundRoleInOrgRoles || foundRoleInBoolean;
            const finalOrg = orgClaim || foundOrgInOrgRoles || Organization; // Fallback to selected org

            console.log(`🔎 Extracted role: ${finalRole}, organization: ${finalOrg}`);
            console.log(`🔎 Claim sources - Direct: ${roleClaim}, orgRoles: ${foundRoleInOrgRoles}, Boolean: ${foundRoleInBoolean}`);

            // ✅ STEP 4: Validate claims
          if (finalRole) {
            console.log("✅ SUCCESS: Claims verified successfully:", { 
              role: roleClaim, 
              organization: orgClaim 
            });
            
            // Store in localStorage for immediate use
            localStorage.setItem('currentUserRole', finalRole);
            localStorage.setItem('currentUserOrgName', finalOrg);
            
            // Clear debug info on success
            debugInfo.value = null;
            
            return { 
              success: true, 
              claims: { role: roleClaim, organization: orgClaim }
            };
          }
          
          // ✅ STEP 5: If not the last attempt, wait before retrying
          if (attempt < maxRetries) {
            console.log(`⏳ Claims not ready, waiting ${delay}ms before retry...`);
            debugInfo.value = `Attempt ${attempt}: Waiting for claims to propagate...`;
            
            await new Promise(resolve => setTimeout(resolve, delay));
            
            // Exponential backoff with jitter
            delay = Math.min(delay * 1.5, 10000); // Max 10 seconds
            delay += Math.random() * 500; // Add jitter
          }
        } catch (error) {
          console.error(`❌ Attempt ${attempt} - Error verifying claims:`, error);
          debugInfo.value = `Attempt ${attempt} error: ${error.message}`;
          
          if (attempt < maxRetries) {
            console.log(`⏳ Retrying in ${delay}ms after error...`);
            await new Promise(resolve => setTimeout(resolve, delay));
            delay = Math.min(delay * 1.5, 10000);
            delay += Math.random() * 500;
          }
        }
      }
      
      console.error("❌ FAILED: Claims verification failed after all attempts");
      debugInfo.value = `Failed after ${maxRetries} attempts. Last error: Claims not found`;
      return { success: false, error: "Claims verification failed after all retries" };
    };

    const signup = async () => {
      if (password.value !== confirmPassword.value) {
        signupError.value = "Passwords do not match.";
        return;
      }

      const tokenId = (inputToken.value || "").trim();
      if (!tokenId) {
        signupError.value = "Enter a token.";
        return;
      }

      loading.value = true;
      signupError.value = '';
      claimSetStatus.value = null;
      debugInfo.value = null;
      retryCount.value = 0;

      try {
        console.log("🚀 Starting signup process for:", email.value);
        debugInfo.value = "Creating user account...";
        
        // 1) Find and validate token
        const tokensQuery = query(collectionGroup(db, "Tokens"), where("TokenID", "==", tokenId), limit(1));
        const tokenSnap = await getDocs(tokensQuery);
        if (tokenSnap.empty) throw new Error("Invalid or expired token");

        const tokenDoc = tokenSnap.docs[0];
        const token = tokenDoc.data();
        const Role = token.Role;
        console.log("✅ Token found with role:", Role);
        debugInfo.value = "Token validated. Extracting organization...";

        // Get Organization from parent document
        const tokenDocPath = tokenDoc.ref.path;
        const parentDocId = tokenDocPath.split("/")[1];
        const settingsRef = doc(db, "ADMINsettings", parentDocId);
        const settingsSnap = await getDoc(settingsRef);
        if (!settingsSnap.exists()) throw new Error("Parent settings doc missing");
        const Organization = settingsSnap.get("Organization");
        console.log("✅ Organization extracted:", Organization);
        debugInfo.value = "Organization extracted. Creating user document...";

        // 2) Create Auth user
        const userCredential = await createUserWithEmailAndPassword(auth, email.value, password.value);
        const user = userCredential.user;
        const uid = user.uid;
        console.log("✅ Firebase auth user created:", uid);

        // 3) Write to users/{uid}
        const userRef = doc(db, "users", uid);
        await setDoc(userRef, {
          email: user.email,
          Roles: [{ Organization, Role, assignedAt: new Date().toISOString(), source: "token" }],
          LastLogin: serverTimestamp(),
          source: "signup",
        });
        console.log("✅ User document created in Firestore");
        debugInfo.value = "User document created. Setting claims...";

        // 4) Set custom claims via Cloud Function
        console.log("🔄 Setting custom claims via Cloud Function...");
        console.log("📤 Cloud Function parameters:", { uid, role: Role, organization: Organization, tokenDocPath });
        
        processingClaims.value = true;
        debugInfo.value = "Calling Cloud Function to set claims...";
        
        const cloudFunctionResult = await setInitialClaims({ uid, role: Role, organization: Organization, tokenDocPath, });
        console.log("✅ Cloud Function response:", cloudFunctionResult);

        // ✅ ENHANCED: Use improved claim verification
        const claimResult = await verifyClaimsWithRetry(user);
        
        if (!claimResult.success) {
          console.error("❌ Claims verification failed");
          throw new Error("Claims were not set properly after multiple attempts. The Cloud Function may have failed or there may be a propagation delay. Please contact support with the following details: UID=" + uid + ", Organization=" + Organization + ", Role=" + Role);
        }

        claimSetStatus.value = {
          success: true,
          message: `Account configured successfully! Role: ${claimResult.claims.role}`
        };

        // 5) Send verification email
        console.log("📧 Sending verification email...");
        await sendEmailVerification(user);
        console.log("✅ Verification email sent to:", user.email);
        
        // Show success dialog
        showDialog.value = true;

      } catch (error) {
        console.error("❌ Signup failed:", error);
        console.error("Full error details:", {
          code: error.code,
          message: error.message,
          details: error.details,
          stack: error.stack
        });

        const reason = error?.details?.reason || error?.message?.split(":")[0] || "UNKNOWN_ERROR";

        const messages = {
          TOKEN_NOT_FOUND: "Token not found. Double-check the code.",
          TOKEN_ALREADY_USED: "This token has already been used.",
          ORGANIZATION_MISSING: "Admin settings missing Organization.",
          ROLE_MISSING: "Token is missing its Role.",
          TOKEN_PARENT_MISSING: "Token parent document missing.",
          SETTINGS_DOC_MISSING: "Admin settings document not found.",
          MISSING_TOKEN: "Enter a token.",
          MISSING_UID: "Missing user id.",
          UID_MISMATCH: "Cannot set claims for another user.",
          NOT_AUTHENTICATED: "You must be signed in.",
        };

        signupError.value = messages[reason] || error.message || "Signup failed. Please try again.";
        
        // If claims failed, show specific status
        if (error.message.includes("Claims were not set properly")) {
          claimSetStatus.value = {
            success: false,
            message: "Account created but role setup failed. Please contact support."
          };
          debugInfo.value = "Claims setup failed. See error message above.";
        }
        
        return;
      } finally {
        loading.value = false;
        processingClaims.value = false;
        retryCount.value = 0;
      }
    };

    const showDialog = ref(false);
    const resending = ref(false);

    const resendVerification = async () => {
      try {
        resending.value = true;
        if (auth.currentUser) {
          await sendEmailVerification(auth.currentUser);
          // You might want to show a success message
        }
      } catch (err) {
        console.error("Resend failed:", err);
        signupError.value = "Failed to resend verification email.";
      } finally {
        resending.value = false;
      }
    };

    const goToLogin = () => {
      router.push('/login');
    };

    return { 
      email, 
      password, 
      confirmPassword, 
      inputToken, 
      showPassword, 
      signup, 
      isFormValid, 
      signupError, 
      loading,
      showDialog, 
      resendVerification, 
      resending, 
      router,
      goToLogin,
      processingClaims,
      claimSetStatus,
      retryCount,
      debugInfo
    };
  },
};
</script>

<style scoped>
/* Use the same background styling as LoginPage */
.page-background {
  background-image: url('../assets/frontpage 01.jpg');
  background-size: cover;
  background-position: center;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
}

.layout-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 55%;
  gap: 30px;
}

.info-container {
  text-align: left;
  color: white;
  max-width: 500px;
  height: 600px;
  padding: 20px;
  border: 2px solid white;
  border-radius: 10px;
  background-color: rgba(4, 4, 123, 0.648);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.mn-logo {
  width: 60px;
  height: auto;
  margin-bottom: 20px;
}

.info-container h2 {
  font-size: 60px;
  margin-bottom: 30px;
  text-align: center;
  font-weight: bold;
  width: 100%;
}

.MR-description {
  font-size: 22px;
  line-height: 1.5;
  margin-left: 20px;
  margin-right: 20px;
  margin-bottom: 40px;
  color: white;
}

.copywrite {
  font-size: 14px;
  font-style: italic;
  color: white !important;
  margin-left: 20px;
  margin-right: 20px;
  text-align: center;
  display: block;
  margin-bottom: 20px;
}

.form-container {
  background-color: rgba(255, 255, 255, 0.8);
  padding: 40px;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  width: 100%;
  max-width: 400px;
  text-align: center;
}

.form-container h1 {
  margin-bottom: 20px;
  color: #333;
}

.input-container {
  margin-bottom: 15px;
  text-align: left;
}

.checkbox-container {
  display: flex;
  align-items: center;
}

.checkbox-container label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  cursor: pointer;
}

.form-container input[type="text"],
.form-container input[type="email"],
.form-container input[type="password"] {
  height: 45px;
  width: 100%;
  padding: 12px;
  border: 2px solid #ccc;
  border-radius: 5px;
  font-size: 16px;
  transition: border-color 0.3s;
}

.form-container input[type="text"]:focus,
.form-container input[type="email"]:focus,
.form-container input[type="password"]:focus {
  outline: none;
  border-color: #007bff;
}

.form-container input.error {
  border-color: #dc3545;
}

.form-container input.success {
  border-color: #28a745;
}

.form-container button {
  width: 100%;
  padding: 12px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 5px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.form-container button:hover:not(:disabled) {
  background-color: #0056b3;
}

.form-container button:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}

.newuser {
  margin-top: 25px;
  color: #666;
}

.newuser a {
  color: #007bff;
  text-decoration: none;
}

.newuser a:hover {
  text-decoration: underline;
}

.error-message {
  color: #dc3545;
  font-size: 14px;
  margin-top: 10px;
  padding: 8px;
  background-color: rgba(220, 53, 69, 0.1);
  border-radius: 4px;
  border: 1px solid rgba(220, 53, 69, 0.3);
}

.spinner {
  border: 2px solid transparent;
  border-top: 2px solid #fff;
  border-radius: 50%;
  width: 15px;
  height: 15px;
  animation: spin 1s linear infinite;
  margin-right: 10px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Responsive Design */
@media (max-width: 1200px) {
  .layout-container {
    width: 70%;
    flex-direction: column;
    gap: 20px;
  }
  
  .info-container {
    max-width: 100%;
    height: auto;
    min-height: 400px;
  }
  
  .info-container h2 {
    font-size: 40px;
  }
  
  .MR-description {
    font-size: 18px;
  }
}

@media (max-width: 768px) {
  .layout-container {
    width: 90%;
    padding: 20px;
  }
  
  .info-container {
    padding: 15px;
  }
  
  .info-container h2 {
    font-size: 30px;
    margin-bottom: 20px;
  }
  
  .MR-description {
    font-size: 16px;
    margin-bottom: 20px;
  }
  
  .form-container {
    padding: 30px 20px;
  }
}

@media (max-width: 480px) {
  .layout-container {
    width: 95%;
  }
  
  .form-container {
    padding: 20px 15px;
  }
  
  .info-container h2 {
    font-size: 24px;
  }
  
  .MR-description {
    font-size: 14px;
  }
}
</style>